import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ArrowUpRight } from 'lucide-react';

/**
 * "Glass" CRM page language (Nov 2026 test on /crm/lost-leads-reports):
 * smoky warm backdrop, translucent 28px cards, outlined icon chips, pastel
 * chart palette, one lime accent, and pill switchers whose highlight slides
 * with a leading-edge-first clip-path animation. Theme-aware: dark is the
 * reference look, light is a warm frosted variant.
 */

export const GLASS_ACCENT = '#D7F36B';

// Pastel series colours that read on both the dark and the light backdrop
export const GLASS_PALETTE = ['#D7F36B', '#F2967E', '#93B4F5', '#7FD9B0', '#B9A3F5', '#F28BA8', '#E8C88A', '#6FCFCF', '#C9D87A', '#F5B58A', '#8EC5F0', '#D8A3E8', '#A6E3C8', '#F0A0A0'];

export const GLASS_TONE = {
  lime: '#D7F36B',
  salmon: '#F2967E',
  blue: '#93B4F5',
  mint: '#7FD9B0',
  lavender: '#B9A3F5',
};

// Spelled out so Tailwind's JIT keeps every class
export const GLASS_TEXT = {
  lime: 'text-lime-700 dark:text-[#D7F36B]',
  salmon: 'text-[#C8553A] dark:text-[#F2967E]',
  blue: 'text-[#4169C4] dark:text-[#93B4F5]',
  mint: 'text-emerald-600 dark:text-[#7FD9B0]',
  muted: 'text-gray-500 dark:text-white/[0.45]',
  strong: 'text-gray-900 dark:text-white',
};

const BADGE_TONES = {
  lime: 'bg-[#D7F36B] text-gray-900',
  salmon: 'bg-[#F2967E] text-gray-900',
  blue: 'bg-[#93B4F5] text-gray-900',
  mint: 'bg-[#7FD9B0] text-gray-900',
  lavender: 'bg-[#B9A3F5] text-gray-900',
  neutral: 'bg-black/[0.06] text-gray-700 dark:bg-white/[0.08] dark:text-white/70',
};

const SOFT_BADGE_TONES = {
  lime: 'bg-lime-500/[0.15] text-lime-800 dark:bg-[#D7F36B]/[0.15] dark:text-[#D7F36B]',
  salmon: 'bg-[#F2967E]/20 text-[#B5472C] dark:bg-[#F2967E]/[0.15] dark:text-[#F2967E]',
  blue: 'bg-[#93B4F5]/25 text-[#3657A8] dark:bg-[#93B4F5]/[0.15] dark:text-[#93B4F5]',
  mint: 'bg-[#7FD9B0]/25 text-emerald-800 dark:bg-[#7FD9B0]/[0.15] dark:text-[#7FD9B0]',
  lavender: 'bg-[#B9A3F5]/25 text-[#5B43A8] dark:bg-[#B9A3F5]/[0.15] dark:text-[#B9A3F5]',
  neutral: 'bg-black/[0.05] text-gray-600 dark:bg-white/[0.06] dark:text-white/60',
};

export const GLASS_FIELD = 'w-full rounded-xl border border-black/10 bg-white/70 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D7F36B]/70 disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/30';

export const GLASS_TH = 'pb-3 pr-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-white/40';

export const GLASS_ROW = 'border-t border-black/[0.06] transition-colors hover:bg-black/[0.025] dark:border-white/[0.06] dark:hover:bg-white/[0.035]';

export const GLASS_INSET = 'rounded-2xl border border-black/[0.05] bg-black/[0.03] dark:border-white/[0.06] dark:bg-white/[0.035]';

const CARD = 'relative rounded-[28px] border border-white/70 bg-white/[0.55] shadow-[0_10px_40px_-18px_rgba(70,45,20,0.25),inset_0_1px_0_rgba(255,255,255,0.8)] backdrop-blur-2xl dark:border-white/[0.08] dark:bg-white/[0.04] dark:bg-gradient-to-b dark:from-white/[0.05] dark:to-white/[0.01] dark:shadow-[0_24px_60px_-28px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(255,255,255,0.06)]';

// Keyframes for the page: card rise-in and the slow backdrop drift
export const GlassStyles = () => (
  <style>{`
    @keyframes glass-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
    @keyframes glass-drift-a { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(4vw, 3vh) scale(1.08); } }
    @keyframes glass-drift-b { 0%, 100% { transform: translate(0, 0) scale(1.05); } 50% { transform: translate(-5vw, -4vh) scale(0.95); } }
    .glass-rise { animation: glass-rise 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
    .glass-drift-a { animation: glass-drift-a 26s ease-in-out infinite; }
    .glass-drift-b { animation: glass-drift-b 32s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .glass-rise, .glass-drift-a, .glass-drift-b { animation: none; }
    }
  `}</style>
);

// Smoky warm backdrop: soft blurred glows over a deep base, fixed behind the page
export const GlassBackdrop = () => (
  <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden bg-[#EDE9E3] dark:bg-[#0d0c0b]">
    <div className="glass-drift-a absolute -top-[25%] left-[20%] h-[75vh] w-[70vw] rounded-full bg-[#E3B98C]/50 blur-[120px] dark:bg-[#8C5C34]/40" />
    <div className="glass-drift-b absolute top-[5%] -left-[20%] h-[65vh] w-[55vw] rounded-full bg-[#B9C3CC]/60 blur-[120px] dark:bg-[#4E5A66]/[0.35]" />
    <div className="glass-drift-a absolute -bottom-[30%] -right-[15%] h-[75vh] w-[60vw] rounded-full bg-[#D8B49A]/[0.45] blur-[140px] dark:bg-[#5A3A26]/[0.45]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,transparent_35%,rgba(120,95,70,0.12)_100%)] dark:bg-[radial-gradient(ellipse_at_50%_20%,transparent_30%,rgba(0,0,0,0.55)_100%)]" />
  </div>
);

export const GlassCard = ({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) => (
  <Tag className={`${CARD} glass-rise ${className}`} style={{ animationDelay: `${delay}ms` }} {...rest}>
    {children}
  </Tag>
);

// Outlined circle button, used for the back arrow and card "open" arrows
export const GlassCircleButton = ({ to, onClick, title, children, className = '' }) => {
  const cls = `grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-black/10 bg-white/40 text-gray-700 transition hover:bg-white/80 hover:text-gray-900 dark:border-white/[0.15] dark:bg-white/[0.04] dark:text-white/70 dark:hover:bg-white/[0.1] dark:hover:text-white ${className}`;
  if (to) return <Link to={to} title={title} aria-label={title} className={cls}>{children}</Link>;
  return <button type="button" onClick={onClick} title={title} aria-label={title} className={cls}>{children}</button>;
};

export const GlassCardHeader = ({ title, to, toTitle, right, className = '' }) => (
  <div className={`flex items-center justify-between gap-3 ${className}`}>
    <h2 className="text-[15px] font-medium text-gray-800 dark:text-white/[0.85]">{title}</h2>
    <div className="flex items-center gap-2">
      {right}
      {to && (
        <GlassCircleButton to={to} title={toTitle || `Open ${title}`}>
          <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
        </GlassCircleButton>
      )}
    </div>
  </div>
);

// Big figure with a muted caption, like "$439,011 won, last 6 months"
export const GlassFigure = ({ value, caption, className = '' }) => (
  <div className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 ${className}`}>
    <span className="text-4xl font-medium tracking-tight text-gray-900 tabular-nums dark:text-white">{value}</span>
    {caption && <span className="text-xs text-gray-500 dark:text-white/[0.45]">{caption}</span>}
  </div>
);

// Header KPI: outlined icon circle, value with optional badge, short caption
export const GlassStat = ({ icon: Icon, value, label, badge, badgeTone = 'salmon', valueClassName = '' }) => (
  <div className="flex items-center gap-3">
    <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-full border border-black/10 text-gray-700 dark:border-white/[0.15] dark:text-white/75">
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
    </span>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className={`text-xl font-semibold tabular-nums ${valueClassName || 'text-gray-900 dark:text-white'}`}>{value}</span>
        {badge && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${BADGE_TONES[badgeTone] || BADGE_TONES.neutral}`}>{badge}</span>}
      </div>
      <div className="max-w-[10rem] text-xs leading-snug text-gray-500 dark:text-white/[0.45]">{label}</div>
    </div>
  </div>
);

// Legend chip: dot, label, value
export const GlassChip = ({ color, label, value }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-black/[0.05] bg-black/[0.04] px-3 py-1.5 text-xs dark:border-white/[0.06] dark:bg-white/[0.06]">
    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
    <span className="font-medium text-gray-800 dark:text-white/[0.85]">{label}</span>
    {value !== undefined && <span className="text-gray-500 tabular-nums dark:text-white/[0.45]">{value}</span>}
  </span>
);

export const GlassBadge = ({ tone = 'neutral', children }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${SOFT_BADGE_TONES[tone] || SOFT_BADGE_TONES.neutral}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {children}
  </span>
);

export const GlassAccentButton = ({ children, className = '', ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center gap-2 rounded-full bg-[#D7F36B] px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-[0_8px_24px_-10px_rgba(190,230,80,0.8)] transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none ${className}`}
    {...rest}
  >
    {children}
  </button>
);

const prefersReducedMotion = () => typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Pill switcher. A clipped highlight layer (with its own copy of the labels)
 * slides between options; the leading edge moves first and faster, the
 * trailing edge catches up, so the pill stretches then settles.
 */
export const SlidingPills = ({ options, value, onChange, size = 'md', className = '', ariaLabel }) => {
  const boxRef = useRef(null);
  const pillRef = useRef(null);
  const btnRefs = useRef({});
  const edges = useRef(null);

  const itemCls = size === 'sm' ? 'px-3.5 py-1.5 text-xs' : 'px-5 py-2 text-sm';

  const place = useCallback((animate) => {
    const box = boxRef.current;
    const pill = pillRef.current;
    const target = btnRefs.current[value];
    if (!box || !pill || !target) return;
    const W = box.scrollWidth, H = box.clientHeight;
    const L = target.offsetLeft;
    const R = W - (target.offsetLeft + target.offsetWidth);
    const T = target.offsetTop;
    const B = H - (target.offsetTop + target.offsetHeight);
    const clip = (l, r) => `inset(${T}px ${r}px ${B}px ${l}px round 999px)`;
    const o = edges.current;
    if (!o || !animate || prefersReducedMotion()) {
      if (o) gsap.killTweensOf(o);
      edges.current = { l: L, r: R };
      gsap.set(pill, { clipPath: clip(L, R) });
      return;
    }
    gsap.killTweensOf(o);
    const right = L > o.l;
    // the leading edge goes first and faster; the trailing edge catches up
    gsap.to(o, {
      l: L, duration: right ? 0.55 : 0.36,
      ease: right ? 'power3.inOut' : 'power3.out',
      onUpdate: () => gsap.set(pill, { clipPath: clip(o.l, o.r) }),
    });
    gsap.to(o, {
      r: R, duration: right ? 0.36 : 0.55,
      ease: right ? 'power3.out' : 'power3.inOut',
      onUpdate: () => gsap.set(pill, { clipPath: clip(o.l, o.r) }),
    });
  }, [value]);

  const placeRef = useRef(place);
  placeRef.current = place;

  useLayoutEffect(() => { place(true); }, [place]);

  // Snap (no animation) on layout changes; observed once so it never interrupts a slide
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => placeRef.current(false));
    ro.observe(box);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => () => { if (edges.current) gsap.killTweensOf(edges.current); }, []);

  return (
    <div
      ref={boxRef}
      role="tablist"
      aria-label={ariaLabel}
      className={`relative inline-flex items-center rounded-full border border-black/[0.06] bg-black/[0.04] p-1 backdrop-blur-xl dark:border-white/[0.08] dark:bg-white/[0.05] ${className}`}
    >
      {options.map(opt => (
        <button
          key={opt.value}
          ref={el => { btnRefs.current[opt.value] = el; }}
          type="button"
          role="tab"
          aria-selected={opt.value === value}
          onClick={() => onChange(opt.value)}
          className={`relative whitespace-nowrap rounded-full font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-white/60 dark:hover:text-white ${itemCls}`}
        >
          {opt.label}
        </button>
      ))}
      <div
        ref={pillRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center bg-gray-900 p-1 text-white dark:bg-white/[0.16] dark:text-white"
        style={{ clipPath: 'inset(0 100% 0 0)' }}
      >
        {options.map(opt => (
          <span key={opt.value} className={`whitespace-nowrap rounded-full font-medium ${itemCls}`}>{opt.label}</span>
        ))}
      </div>
    </div>
  );
};

// Recharts colours per theme (recharts needs real values, not classes)
export const glassChartTheme = (darkMode) => (darkMode
  ? { grid: 'rgba(255,255,255,0.07)', tick: 'rgba(255,255,255,0.45)', track: 'rgba(255,255,255,0.035)', cursor: 'rgba(255,255,255,0.04)', ringBg: 'rgba(255,255,255,0.06)' }
  : { grid: 'rgba(30,20,10,0.08)', tick: 'rgba(40,30,20,0.5)', track: 'rgba(30,20,10,0.04)', cursor: 'rgba(30,20,10,0.04)', ringBg: 'rgba(30,20,10,0.06)' });

export const GlassTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white/[0.85] px-3 py-2 text-xs shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-[#1c1a18]/90">
      {label !== undefined && label !== '' && <div className="mb-1 text-gray-500 dark:text-white/[0.45]">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.payload?.color || p.payload?.fill || p.color }} />
          <span className="text-gray-700 dark:text-white/75">{p.name}</span>
          <span className="ml-auto pl-3 font-semibold text-gray-900 tabular-nums dark:text-white">{p.value}{unit ? ` ${unit}` : ''}</span>
        </div>
      ))}
    </div>
  );
};

// Pastel initials circle, colour picked from the name so it stays stable
export const GlassAvatar = ({ name }) => {
  const clean = (name || '').trim();
  const initials = clean
    ? clean.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';
  let hash = 0;
  for (let i = 0; i < clean.length; i += 1) hash = (hash * 31 + clean.charCodeAt(i)) % 997;
  const bg = ['#E9F7C2', '#FBD9CF', '#D6E3FB', '#CFF1E2', '#E3DBFB', '#FAD5E1', '#F3E4C6'][hash % 7];
  return (
    <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-[11px] font-semibold text-gray-800" style={{ backgroundColor: bg }}>
      {initials}
    </span>
  );
};
